import { ExportHistoryItem } from 'types/report';
import { formatDistanceToNow } from 'date-fns';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import CircularProgress from 'components/ui/CircularProgress';

interface NotificationItemProps {
  item: ExportHistoryItem;
  onDownload: (item: ExportHistoryItem) => void;
  onDelete: (item: ExportHistoryItem) => void;
}

const NotificationItem = ({ item, onDownload, onDelete }: NotificationItemProps) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          icon: 'CheckCircle',
          color: 'text-success',
          bgColor: 'bg-success/10',
          label: 'Completed',
        };
      case 'processing':
        return {
          icon: 'Loader',
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          label: 'Processing',
          animate: 'animate-spin',
        };
      case 'queued':
        return {
          icon: 'Clock',
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          label: 'Queued',
        };
      case 'failed':
        return {
          icon: 'XCircle',
          color: 'text-error',
          bgColor: 'bg-error/10',
          label: 'Failed',
        };
      default:
        return {
          icon: 'FileText',
          color: 'text-text-secondary',
          bgColor: 'bg-neutral-100',
          label: 'Unknown',
        };
    }
  };

  const getReportTypeLabel = (reportType: string) => {
    const labels: Record<string, string> = {
      dashboard: 'Summary',
      reservation_analytics: 'Reservation Analytics',
      room_availability: 'Room Availability',
      custom: 'Custom Report',
    };
    return labels[reportType] || reportType;
  };

  const formatFileSize = (size?: string) => {
    if (!size) return '';
    const bytes = parseInt(size, 10);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getTimeAgo = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  const statusConfig = getStatusConfig(item.status);
  const isProcessing = item.status === 'processing' || item.status === 'queued';
  const canInteract = !item.isDownloading && !item.isDeleting;

  return (
    <div className="p-4 hover:bg-neutral-50 transition-colors">
      <div className="flex items-start space-x-3">
        <div className={`flex-shrink-0 w-10 h-10 ${statusConfig.bgColor} rounded-lg flex items-center justify-center`}>
          {isProcessing && item.progress !== undefined ? (
            <CircularProgress progress={item.progress} size={36} strokeWidth={3} />
          ) : (
            <Icon
              name={statusConfig.icon}
              size={20}
              className={`${statusConfig.color} ${statusConfig.animate || ''}`}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-1">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-text-primary truncate">{getReportTypeLabel(item.reportType)}</h3>
              <p className="text-xs text-text-secondary mt-0.5">
                {item.format.toUpperCase()} · {getTimeAgo(item.createdAt)}
              </p>
            </div>
          </div>

          {isProcessing && item.progress !== undefined && (
            <div className="mt-2 mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-text-secondary">{statusConfig.label}</span>
                <span className="text-xs font-medium text-primary">{Math.round(item.progress)}%</span>
              </div>
              <div className="w-full bg-neutral-200 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-primary h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${item.progress}%` }}
                />
              </div>
            </div>
          )}

          {item.status === 'completed' && (
            <div className="flex items-center space-x-2 mt-2 text-xs text-text-secondary">
              {item.filename && (
                <>
                  <Icon name="File" size={12} />
                  <span className="truncate max-w-[180px]">{item.filename}</span>
                </>
              )}
              {item.fileSize && (
                <>
                  <span>·</span>
                  <span>{formatFileSize(item.fileSize)}</span>
                </>
              )}
            </div>
          )}

          {item.status === 'failed' && <p className="text-xs text-error mt-2">Export failed. Please try again.</p>}

          <div className="flex items-center space-x-2 mt-3">
            {item.status === 'completed' && item.canDownload && (
              <Button
                variant="primary"
                size="xs"
                onClick={() => onDownload(item)}
                disabled={!canInteract}
                loading={item.isDownloading}
                className="flex-1"
              >
                <Icon name="Download" size={14} />
                <span className="ml-1.5">Download</span>
              </Button>
            )}

            {(item.status === 'completed' || item.status === 'failed') && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => onDelete(item)}
                disabled={!canInteract}
                loading={item.isDeleting}
                className="text-error hover:bg-error/10"
              >
                <Icon name="Trash2" size={14} />
              </Button>
            )}

            {isProcessing && (
              <div className="flex-1 text-xs text-text-secondary">
                <Icon name="Clock" size={12} className="inline mr-1" />
                {item.expiresAt && <span>Expires {getTimeAgo(item.expiresAt)}</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
